import { useNavigation, useRoute } from '@react-navigation/native';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TouchableOpacity, View } from 'react-native';
import Toast from 'react-native-root-toast';

import { ROLE_PERMISSIONS, POS_TYPE } from '../../../common/constants';
import { useUserContext } from '../../../common/utils/context';

import DotMenu from '../../components/navigation/DotMenu';
import { Icon, Spinner } from '../../components';

import DocketDetail from './DocketDetail';
import withMediaPicker from '../../hocs/withMediaPicker';
import Overlay from '../../components/other/Overlay';
import { CREATE_MEDIA } from '../../../common/graphql/media';
import {
	GET_SERVICE_DOCKET_NATIVE,
	MODIFY_SERVICE_DOCKET,
	SUBSCRIBE_TO_SERVICE_DOCKET_NATIVE,
} from '../../graphql/serviceDocket';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useFreshDetailQueryOptions } from '../../hooks';
import { useOfflineMutation } from '../../offline';
import { useOfflineQueue } from '../../offline';
import { useTheme } from '../../theme';
import TrainingModeBanner from '../myDay/TrainingModeBanner';
import { getMyDayTrainingRouteParams, isMyDayTraining } from '../myDay/myDayTraining';

const DocketDetailViewRaw = ({
	captureMedia,
	capturedMediaLink,
	processingMedia,
}) => {
	const { hasPermission } = useUserContext();
	const navigation = useNavigation();
	const route = useRoute();
	const styles = useThemedStyles(createStyles);
	const trainingContext = route.params?.myDayTraining;
	const isTraining = isMyDayTraining(trainingContext);
	const [processingQuickPhoto, setProcessingQuickPhoto] = useState(false);
	const [createMedia] = useOfflineMutation(CREATE_MEDIA, {
		configKey: 'createMedia',
		disableOffline: isTraining,
	});
	const [refetch, setRefetch] = useState(null);
	const [shouldRefetchAfterUpload, setShouldRefetchAfterUpload] = useState(false);
	const { machineId, docketId, machineName } = route.params || {};
	const lastProcessedLinkRef = useRef(null);
	const initialQuickPhotoTriggeredRef = useRef(false);
	const { isOnline } = useOfflineQueue();
	const isOnlineRef = useRef(isOnline);
	isOnlineRef.current = isOnline;
	const captureContextMedia = useCallback(() => {
		if (isTraining && !isOnlineRef.current) {
			Toast.show('Reconnect to continue training');
			return;
		}
		captureMedia();
	}, [captureMedia, isTraining]);
	const {
		canRenderDetailQuery,
		detailQueryOptions,
		blockDataWhileLoading,
	} = useFreshDetailQueryOptions({ noCache: isTraining });

	// don't add deps to avoid infinite loop
	useEffect(() => {
		if (hasPermission && navigation) {
			const canAddTickets = hasPermission(ROLE_PERMISSIONS.ADD_EDIT_TICKETS);
			navigation.setOptions(getNavigationOptions(canAddTickets, navigation, route, captureContextMedia));
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// on quick photo capture
	useEffect(() => {
		if (!capturedMediaLink || capturedMediaLink === lastProcessedLinkRef.current) {
			return;
		}

		lastProcessedLinkRef.current = capturedMediaLink;

		let isActive = true;
		setProcessingQuickPhoto(true);

		(async () => {
			try {
				const result = await createMedia({
					variables: {
						input: {
							machine: machineId,
							serviceDocket: docketId,
							link: capturedMediaLink,
							...(isTraining ? { adminTraining: true } : {}),
						},
					},
					context: {
						offlineMeta: {
							machineId,
							machineName,
						},
					},
				});

				const isQueued = Boolean(result?.offline);
				if (isQueued) {
					Toast.show('Photo queued; will sync when back online');
				}
				else {
					Toast.show('Photo uploaded successfully');
					if (isOnline && typeof refetch === 'function') {
						await refetch();
					}
					else if (isOnline) {
						setShouldRefetchAfterUpload(true);
					}
				}
			}
			catch (e) {
				if (isOnline) {
					Toast.show('Error when uploading the photo');
				}
				else if (isTraining) {
					Toast.show('Reconnect to continue training');
				}
				else {
					Toast.show('Photo queued; will sync when back online');
				}
			}
			finally {
				if (isActive) {
					setProcessingQuickPhoto(false);
				}
			}
		})();

		return () => {
			isActive = false;
		};
	}, [capturedMediaLink, createMedia, refetch, machineId, docketId, isOnline, isTraining, machineName]);

	useEffect(() => {
		if (!shouldRefetchAfterUpload || !isOnline || typeof refetch !== 'function') {
			return;
		}

		let isActive = true;

		(async () => {
			try {
				await refetch();
			}
			finally {
				if (isActive) {
					setShouldRefetchAfterUpload(false);
				}
			}
		})();

		return () => {
			isActive = false;
		};
	}, [isOnline, refetch, shouldRefetchAfterUpload]);

	useEffect(() => {
		if (!route.params?.openQuickPhoto || initialQuickPhotoTriggeredRef.current) {
			return;
		}

		initialQuickPhotoTriggeredRef.current = true;

		const timeout = setTimeout(() => {
			captureContextMedia();
		}, 0);

		return () => clearTimeout(timeout);
	}, [captureContextMedia, route.params?.openQuickPhoto]);

	return (
		<View style={styles.container}>
			<TrainingModeBanner trainingContext={trainingContext} isOnline={isOnline} />
			{(processingQuickPhoto || processingMedia) && (
				<Overlay />
			)}
			{canRenderDetailQuery ? (
				<DocketDetail
					id={docketId}
					query={GET_SERVICE_DOCKET_NATIVE}
					subscription={isTraining ? undefined : SUBSCRIBE_TO_SERVICE_DOCKET_NATIVE}
					modifyMutation={MODIFY_SERVICE_DOCKET}
					setRefetch={setRefetch}
					detailQueryOptions={detailQueryOptions}
					blockDataWhileLoading={blockDataWhileLoading}
					trainingContext={trainingContext}
				/>
			) : (
				<Spinner />
			)}
		</View>
	);
};

const getNavigationOptions = (canAddTickets, navigation, route, captureMedia) => {
	return {
		headerRight: () => <HeaderActions canAddTickets={canAddTickets} navigation={navigation} route={route} captureMedia={captureMedia} />,
	};
};

const HeaderActions = ({
	canAddTickets,
	navigation,
	route,
	captureMedia,
}) => {
	const { theme } = useTheme();

	return (
		<View style={{ flexDirection: 'row', alignItems: 'center' }}>
			<TouchableOpacity
				onPress={captureMedia}
				style={{
					paddingTop: 6,
					paddingLeft: 12,
					paddingRight: 0,
				}}
			>
				<Icon
					name="camera"
					size={20}
					color={theme.text1}
				/>
			</TouchableOpacity>
			<MenuComponent canAddTickets={canAddTickets} navigation={navigation} route={route} captureMedia={captureMedia} />
		</View>
	);
};

const MenuComponent = ({
	canAddTickets,
	navigation,
	route,
	captureMedia,
}) => {
	const triggerQuickPhoto = () => {
		// Defer camera launch until the dropdown menu is fully dismissed.
		setTimeout(() => {
			captureMedia();
		}, 0);
	};

	const menuOptions = [
		{
			title: 'Quick photo',
			onPress: triggerQuickPhoto,
		},
		{
			divider: true,
		},
		{
			title: 'Add photo',
			onPress: () => {
				navigation.push('MediaListView', {
					docketId: route.params?.docketId,
					machineId: route.params?.machineId,
					machineName: route.params?.machineName,
					machineType: route.params?.machineType,
					isMarket: route.params?.isMarket,
					forcePhotoImmediately: true,
					...getMyDayTrainingRouteParams(route.params?.myDayTraining),
				});
			},
		},
	];

	if (canAddTickets) {
		menuOptions.push({
			title: 'Create ticket',
			onPress: () => {
				navigation.push('TicketCreator', {
					machineId: route.params?.machineId,
					machineName: route.params?.machineName,
					docketId: route.params?.docketId,
					...getMyDayTrainingRouteParams(route.params?.myDayTraining),
				});
			},
		});
	}

	return (
		<DotMenu
			menuOptions={menuOptions}
		/>
	);
};

const WrappedDocketDetailViewRaw = withMediaPicker(DocketDetailViewRaw);

const DocketDetailView = () => {
	const route = useRoute();
	const routeIsMarket = route.params?.isMarket;
	const isMarket = typeof routeIsMarket === 'boolean'
		? routeIsMarket
		: route.params?.machineType === POS_TYPE.MARKET;

	return (
		<WrappedDocketDetailViewRaw
			quickMediaCapture={true}
			allowMultipleCapture={isMarket}
			promptForAnotherCapture={isMarket}
		/>
	);
};

const createStyles = (theme) => ({
	container: {
		backgroundColor: theme.mainBg1,
		flex: 1,
	},
});

export default DocketDetailView;
